import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMYR, fmtL, fmtDate } from "@/lib/format";
import { ClipboardList, CheckCircle2, AlertCircle, Loader2, Gauge, Wallet, TrendingUp } from "lucide-react";

const glass = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/staff/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">
          My Stations · {fmtDate(data?.date)}
        </div>
        <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Hi {user?.name}</h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Here's today's target and what's been entered so far.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : !data?.stations?.length ? (
        <div className={`${glass} p-12 text-center text-[#9CA3AF]`}>
          You have no stations assigned yet. Ask your manager to grant access.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.stations.map((s) => {
            const done = s.pumps_total > 0 && s.pumps_reported >= s.pumps_total && s.cash_entered;
            return (
              <div key={s.station_id} className={`${glass} p-5 sm:p-6 animate-fadeUp`} data-testid={`staff-station-${s.station_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-[Outfit] font-semibold text-xl">{s.name}</h3>
                    <div className="text-xs text-[#9CA3AF] mt-0.5">{s.location}</div>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-1 rounded uppercase flex items-center gap-1 ${done ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                    {done ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {done ? "Complete" : "Pending"}
                  </span>
                </div>

                {/* Daily target: pumps reported */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-[#9CA3AF] mb-1">
                    <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /> Meter readings today</span>
                    <span className="font-mono">{s.pumps_reported}/{s.pumps_total} pumps</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#1F2937] overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${s.pumps_total ? Math.min(100, (s.pumps_reported / s.pumps_total) * 100) : 0}%` }} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4">
                  <Stat icon={TrendingUp} label="Today revenue" value={fmtMYR(s.today_revenue)} />
                  <Stat icon={Gauge} label="Today liters" value={fmtL(s.today_liters)} />
                  <Stat icon={Wallet} label="Cash entered" value={s.cash_entered ? fmtMYR(s.today_cash) : "—"} />
                </div>

                <div className="mt-4 flex items-center justify-between text-xs text-[#9CA3AF]">
                  <span>Month to date: <span className="text-white font-mono">{fmtMYR(s.month_revenue)}</span> · {fmtL(s.month_liters)}</span>
                  <Link to="/operations" data-testid={`enter-data-${s.station_id}`}
                    className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 font-medium">
                    <ClipboardList className="w-4 h-4" /> Enter data
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-3">
      <div className="flex items-center gap-1 text-[10px] text-[#9CA3AF] uppercase tracking-wider"><Icon className="w-3 h-3" /> {label}</div>
      <div className="metric-value text-base font-semibold text-white mt-1">{value}</div>
    </div>
  );
}
